import { useNavigate } from 'react-router-dom'
import { useTheme } from '../components/ThemeProvider'

const Privacy = () => {
  const navigate = useNavigate()
  const { theme } = useTheme()
  const isDark = theme === 'dark'

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-sage-900">
      {/* Header */}
      <div className="flex items-center justify-between p-4 sm:p-6 lg:p-8 bg-white/60 dark:bg-sage-800/60 border-b border-sage-100/30 dark:border-sage-700/30">
        <button
          onClick={() => navigate('/home')}
          className="p-3 rounded-2xl hover:bg-sage-100/60 dark:hover:bg-sage-700/60 transition-colors duration-500 focus:outline-none focus:ring-4 focus:ring-sage-100/50 dark:focus:ring-sage-700/50"
        >
          <svg className="w-5 h-5 text-sage-500 dark:text-sage-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 className="text-lg font-light text-sage-600 dark:text-sage-300">Privacy Policy</h1>
        <div className="w-11"></div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Last Updated */}
        <div className="text-center mb-8">
          <p className="text-sage-400 dark:text-sage-500 text-sm font-light">
            Last updated: January 2025
          </p>
        </div>

        {/* Introduction */}
        <section className="mb-8">
          <div className="bg-white/80 dark:bg-sage-800/80 rounded-3xl border border-sage-100/50 dark:border-sage-700/50 p-6 sm:p-8 shadow-sm">
            <h2 className="text-sage-600 dark:text-sage-300 font-light text-base sm:text-lg mb-4">Your Privacy Matters</h2>
            <p className="text-sage-500 dark:text-sage-400 font-light leading-relaxed mb-4">
              At Mind Polish, we understand that your mental health data is deeply personal. We're committed to protecting your privacy and being transparent about how we handle your information.
            </p>
            <div className="p-4 bg-mint-50/60 dark:bg-mint-900/30 rounded-2xl border border-mint-100/50 dark:border-mint-700/30">
              <p className="text-mint-600 dark:text-mint-300 font-light text-xs sm:text-sm text-center">
                <strong>We never sell or share your personal data.</strong> Your wellbeing journey stays private.
              </p>
            </div>
          </div>
        </section>

        {/* What We Collect */}
        <section className="mb-8">
          <div className="bg-cream-50/60 dark:bg-sage-800/60 rounded-3xl border border-cream-100/50 dark:border-sage-700/50 p-6 sm:p-8">
            <h3 className="text-cream-600 dark:text-cream-300 font-light text-base sm:text-lg mb-6">What Data We Collect</h3>
            <div className="space-y-4">
              <div>
                <h4 className="text-cream-600 dark:text-cream-300 font-light text-sm sm:text-base mb-2">Account Information</h4>
                <p className="text-cream-500 dark:text-cream-400 font-light text-xs sm:text-sm leading-relaxed">
                  Email address and encrypted password when you create an account (optional).
                </p>
              </div>
              
              <div>
                <h4 className="text-cream-600 dark:text-cream-300 font-light text-sm sm:text-base mb-2">Wellness Data</h4>
                <p className="text-cream-500 dark:text-cream-400 font-light text-xs sm:text-sm leading-relaxed">
                  Your journal entries, mood check-ins, mindful moments responses, routine progress, and garden growth data.
                </p>
              </div>
              
              <div>
                <h4 className="text-cream-600 dark:text-cream-300 font-light text-sm sm:text-base mb-2">App Preferences</h4>
                <p className="text-cream-500 dark:text-cream-400 font-light text-xs sm:text-sm leading-relaxed">
                  Settings like quiet mode, accessibility preferences, and notification choices.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Why We Collect */}
        <section className="mb-8">
          <div className="bg-peach-50/60 dark:bg-sage-800/60 rounded-3xl border border-peach-100/50 dark:border-sage-700/50 p-6 sm:p-8">
            <h3 className="text-peach-600 dark:text-peach-300 font-light text-base sm:text-lg mb-6">Why We Collect This Data</h3>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-peach-400 dark:bg-peach-500 rounded-full mt-2 flex-shrink-0"></div>
                <p className="text-peach-500 dark:text-peach-400 font-light text-xs sm:text-sm leading-relaxed">
                  To provide personalized support and track your wellness journey
                </p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-peach-400 dark:bg-peach-500 rounded-full mt-2 flex-shrink-0"></div>
                <p className="text-peach-500 dark:text-peach-400 font-light text-xs sm:text-sm leading-relaxed">
                  To help Dopa learn your patterns and provide better support
                </p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-peach-400 dark:bg-peach-500 rounded-full mt-2 flex-shrink-0"></div>
                <p className="text-peach-500 dark:text-peach-400 font-light text-xs sm:text-sm leading-relaxed">
                  To sync your data across devices when you're signed in
                </p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-peach-400 dark:bg-peach-500 rounded-full mt-2 flex-shrink-0"></div>
                <p className="text-peach-500 dark:text-peach-400 font-light text-xs sm:text-sm leading-relaxed">
                  To improve the app's features and accessibility
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* How It's Stored */}
        <section className="mb-8">
          <div className="bg-mint-50/60 dark:bg-sage-800/60 rounded-3xl border border-mint-100/50 dark:border-sage-700/50 p-6 sm:p-8">
            <h3 className="text-mint-600 dark:text-mint-300 font-light text-base sm:text-lg mb-6">How Your Data is Stored</h3>
            <div className="space-y-4">
              <div>
                <h4 className="text-mint-600 dark:text-mint-300 font-light text-sm sm:text-base mb-2">Secure Cloud Storage</h4>
                <p className="text-mint-500 dark:text-mint-400 font-light text-xs sm:text-sm leading-relaxed">
                  When signed in, your data is encrypted and stored securely using Supabase (built on AWS infrastructure).
                </p>
              </div>
              
              <div>
                <h4 className="text-mint-600 dark:text-mint-300 font-light text-sm sm:text-base mb-2">Local Storage Fallback</h4>
                <p className="text-mint-500 dark:text-mint-400 font-light text-xs sm:text-sm leading-relaxed">
                  If you use the app without an account, data stays locally on your device and isn't shared anywhere.
                </p>
              </div>
              
              <div>
                <h4 className="text-mint-600 dark:text-mint-300 font-light text-sm sm:text-base mb-2">Encryption</h4>
                <p className="text-mint-500 dark:text-mint-400 font-light text-xs sm:text-sm leading-relaxed">
                  All personal data is encrypted both in transit and at rest. Even our team cannot read your private content.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Your Rights */}
        <section className="mb-8">
          <div className="bg-sage-50/60 dark:bg-sage-800/60 rounded-3xl border border-sage-100/50 dark:border-sage-700/50 p-6 sm:p-8">
            <h3 className="text-sage-600 dark:text-sage-300 font-light text-base sm:text-lg mb-6">Your Rights & Control</h3>
            <div className="space-y-4">
              <div>
                <h4 className="text-sage-600 dark:text-sage-300 font-light text-sm sm:text-base mb-2">Access Your Data</h4>
                <p className="text-sage-500 dark:text-sage-400 font-light text-xs sm:text-sm leading-relaxed">
                  You can view, export, or download all your data at any time through the app settings.
                </p>
              </div>
              
              <div>
                <h4 className="text-sage-600 dark:text-sage-300 font-light text-sm sm:text-base mb-2">Delete Your Data</h4>
                <p className="text-sage-500 dark:text-sage-400 font-light text-xs sm:text-sm leading-relaxed">
                  You can delete individual entries or your entire account. Deletion is permanent and immediate.
                </p>
              </div>
              
              <div>
                <h4 className="text-sage-600 dark:text-sage-300 font-light text-sm sm:text-base mb-2">Data Portability</h4>
                <p className="text-sage-500 dark:text-sage-400 font-light text-xs sm:text-sm leading-relaxed">
                  Export your data in standard formats to take with you if you choose to leave.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Compliance */}
        <section className="mb-8">
          <div className="bg-neutral-50/60 dark:bg-sage-800/60 rounded-3xl border border-neutral-100/50 dark:border-sage-700/50 p-6 sm:p-8">
            <h3 className="text-neutral-600 dark:text-neutral-300 font-light text-base sm:text-lg mb-6">Legal Compliance</h3>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-neutral-400 dark:bg-neutral-500 rounded-full mt-2 flex-shrink-0"></div>
                <p className="text-neutral-500 dark:text-neutral-400 font-light text-xs sm:text-sm leading-relaxed">
                  <strong>GDPR Compliant:</strong> We respect all European privacy rights
                </p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-neutral-400 dark:bg-neutral-500 rounded-full mt-2 flex-shrink-0"></div>
                <p className="text-neutral-500 dark:text-neutral-400 font-light text-xs sm:text-sm leading-relaxed">
                  <strong>CCPA Compliant:</strong> California privacy rights are fully supported
                </p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-neutral-400 dark:bg-neutral-500 rounded-full mt-2 flex-shrink-0"></div>
                <p className="text-neutral-500 dark:text-neutral-400 font-light text-xs sm:text-sm leading-relaxed">
                  <strong>Mental Health Sensitivity:</strong> We follow best practices for sensitive health data
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section>
          <div className="bg-white/80 dark:bg-sage-800/80 rounded-3xl border border-sage-100/50 dark:border-sage-700/50 p-6 sm:p-8 shadow-sm text-center">
            <h3 className="text-sage-600 dark:text-sage-300 font-light text-base sm:text-lg mb-4">Questions About Privacy?</h3>
            <p className="text-sage-500 dark:text-sage-400 font-light text-xs sm:text-sm leading-relaxed mb-6">
              We're here to help you understand how your data is protected. Don't hesitate to reach out.
            </p>
            <button
              onClick={() => navigate('/contact')}
              className="px-4 sm:px-6 py-2 sm:py-3 bg-sage-100/60 hover:bg-sage-200/50 
                         dark:bg-sage-700/40 dark:hover:bg-sage-600/50
                         text-sage-600 dark:text-sage-300 rounded-2xl text-xs sm:text-sm font-light 
                         border border-sage-200/40 dark:border-sage-600/40 
                         hover:border-sage-300/50 dark:hover:border-sage-500/50
                         transition-colors duration-300 focus:outline-none focus:ring-4 
                         focus:ring-sage-100/50 dark:focus:ring-sage-600/30"
            >
              Contact Us About Privacy
            </button>
          </div>
        </section>
      </div>
    </div>
  )
}

export default Privacy